import type { ExternalTableSchema } from '@packages/table-builder';

/**
 * DynamicTableV2 schema for the Customer (Vendor Company) List page.
 *
 * Migrated from src/pages/vendor_customer/index.tsx.
 * Source endpoint: /api/customer/api/app/vendor-company (GET)
 * Action IDs follow VERB_DOMAIN convention — last word = domain name.
 * Handlers are self-registering Action objects in domains/customer/customer.actions.ts,
 * auto-loaded by DynamicTableV2 via ActionRegistry.ensureActions().
 *
 * Columns aligned with legacy schema (isSubHeader=false = always visible;
 * isSubHeader=true = visibility:'picker' (hidden by default; Add Columns picker).
 */
export const customerTable: ExternalTableSchema = {
  metadata: {
    title: 'Customers',
    entity: 'Customer',
    description: 'View all the users present in your company.',
  },

  dataSource: {
    endpoint: '/api/customer/api/app/vendor-company',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
    },

    filters: [
      {
        name: 'FromOnboardedDate',
        semanticId: 'fromOnboardedDate',
        placeholder: 'Onboarded Date From:',
        type: 'date',
      },
      {
        name: 'ToOnboardedDate',
        semanticId: 'toOnboardedDate',
        placeholder: 'Onboarded Date To:',
        type: 'date',
        dependsOn: 'fromOnboardedDate',
      },
      {
        name: 'StatusIds',
        semanticId: 'statusIds',
        placeholder: 'Status',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/customer/api/app/vendor-company/company-statuses',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'BrokerCompanyId',
        semanticId: 'brokerCompanyId',
        placeholder: 'Broker',
        type: 'asyncSelect',
        multiple: false,
        dataSource: {
          url: '/api/customer/broker/options',
          labelField: 'name',
          valueField: 'companyId',
        },
      },
      {
        name: 'TransferAgentCompanyId',
        semanticId: 'transferAgentCompanyId',
        placeholder: 'Transfer Agent',
        type: 'asyncSelect',
        multiple: false,
        dataSource: {
          url: '/api/customer/company/transfer-agent/options',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'CompanyTypes',
        semanticId: 'companyTypes',
        placeholder: 'Types',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/identity/api/app/compant-type/tenant-types-for-company-create',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'FeatureSetIds',
        semanticId: 'featureSetIds',
        placeholder: 'Feature Set',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/identity/feature-set/options',
          labelField: 'name',
          valueField: 'id',
        },
      },
      {
        name: 'CommissionId',
        semanticId: 'commissionId',
        placeholder: 'Commission',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/commission/commission-list',
          labelField: 'name',
          valueField: 'id',
        },
      },
    ],
  },

  columns: [
    // ── Always-visible columns (isSubHeader: false in legacy) ────────────────
    {
      field: 'name',
      header: 'Company Name',
      type: 'couple',
      sortable: true,
      width: 200,
      couple: {
        titleField: 'name',
        subtitleField: 'accountId',
        subtitlePretext: '#',
        showTag: true,
      },
    },
    {
      field: 'ticker',
      header: 'Ticker',
      type: 'text',
      sortable: true,
      width: 200,
    },
    {
      field: 'primaryStockExchangeName',
      header: 'Stock Exchange',
      type: 'text',
      sortable: true,
      width: 200,
    },
    {
      field: 'maxSandboxLimit',
      header: 'Max Sandbox Limit',
      type: 'text',
      sortable: false,
      width: 200,
    },
    {
      field: 'sandboxUsed',
      header: 'Sandbox Used',
      type: 'text',
      sortable: false,
      width: 200,
    },
    {
      field: 'creationTime',
      header: 'Onboarded Date',
      type: 'date',
      sortable: true,
      width: 200,
    },
    {
      field: 'statusName',
      header: 'Status',
      type: 'badge',
      sortable: true,
      width: 150,
      align: 'center',
      uppercase: true,
      badge: {
        statusMap: {
          Active: 'success',
          active: 'success',
          Inactive: 'info',
          inactive: 'info',
          Terminated: 'error',
          terminated: 'error',
          'Historical Access': 'default',
        },
        defaultVariant: 'default',
      },
    },

    // ── Picker columns (isSubHeader: true in legacy — Add Columns) ───────────
    {
      field: 'state',
      header: 'State',
      type: 'text',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'brokerCompanyName',
      header: 'Broker',
      type: 'text',
      sortable: false,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'transferAgentCompanyName',
      header: 'Transfer Agent',
      type: 'text',
      sortable: false,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'companyTypeNames',
      header: 'Types',
      type: 'text',
      sortable: false,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'featureSetNames',
      header: 'Feature Set',
      type: 'text',
      sortable: false,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'commissionName',
      header: 'Commission',
      type: 'text',
      sortable: false,
      width: 200,
      visibility: 'picker',
    },
    {
      field: 'joinedDate',
      header: 'Joined Date',
      type: 'date',
      sortable: true,
      width: 200,
      visibility: 'picker',
    },
  ],

  actions: {
    row: [
      { id: 'VIEW_CUSTOMER', label: 'View', semanticId: 'viewAction' },
      { id: 'EDIT_CUSTOMER', label: 'Edit', semanticId: 'editAction' },
      { id: 'RESEND_EMAIL_CUSTOMER', label: 'Resend Email', semanticId: 'resendEmailAction' },
      { id: 'UPDATE_STATUS_CUSTOMER', label: 'Update Status', semanticId: 'updateStatusAction' },
      { id: 'OUTBOUND_SSO_CUSTOMER', label: 'Outbound SSO', semanticId: 'outboundSsoAction' },
      {
        id: 'ADVISOR_API_CONFIG_CUSTOMER',
        label: 'Advisor API Config',
        semanticId: 'advisorApiConfigAction',
      },
      { id: 'CONFIG_SANDBOX_CUSTOMER', label: 'Config Sandbox', semanticId: 'configSandboxAction' },
      {
        id: 'FIX_CONFIGURATION_CUSTOMER',
        label: 'Fix Configuration',
        semanticId: 'fixConfigurationAction',
        visibilityCondition: {
          field: 'isFixChannelConfigured',
          operator: 'EQUAL',
          value: true,
        },
      },
      {
        id: 'CALCULATE_CASH_BALANCE_CUSTOMER',
        label: 'Calculate Cash Balance',
        semanticId: 'calculateCashBalanceAction',
      },
    ],

    toolbar: {
      primary: {
        id: 'ADD_CUSTOMER',
        label: 'Add Customer',
        icon: 'add_circle_outline',
      },
    },
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    columnPreferences: { persistenceKey: 'Vendor Customers', resizable: true, reorderable: true },
  },
};
